// src/services/cache/apiCache.ts
import { AxiosResponse } from 'axios';

interface CacheEntry {
  data: any;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

interface CacheConfig {
  ttl: number; // Default TTL in milliseconds
  maxSize: number; // Maximum number of entries
}

class APICache {
  private cache = new Map<string, CacheEntry>();
  private config: CacheConfig;

  constructor(config: CacheConfig = { ttl: 5 * 60 * 1000, maxSize: 100 }) {
    this.config = config;
  }

  // Generate cache key from URL and params
  private generateKey(url: string, method: string = 'GET', params?: any): string {
    const paramString = params ? JSON.stringify(params) : '';
    return `${method}:${url}:${paramString}`;
  }

  // Check if cache entry is still valid
  private isValid(entry: CacheEntry): boolean {
    return Date.now() - entry.timestamp < entry.ttl;
  }

  // Get cached response
  get(url: string, method?: string, params?: any): any | null {
    const key = this.generateKey(url, method, params);
    const entry = this.cache.get(key);
    
    if (entry && this.isValid(entry)) {
      return entry.data;
    }
    
    // Remove expired entry
    if (entry) {
      this.cache.delete(key);
    }
    
    return null;
  }

  // Set cache entry
  set(url: string, data: any, method: string = 'GET', params?: any, customTtl?: number): void {
    // Enforce max size by removing oldest entries
    if (this.cache.size >= this.config.maxSize) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey) {
        this.cache.delete(firstKey);
      }
    }

    const key = this.generateKey(url, method, params);
    const entry: CacheEntry = {
      data,
      timestamp: Date.now(),
      ttl: customTtl || this.config.ttl
    };
    
    this.cache.set(key, entry);
  }

  // Clear specific cache entry
  clear(url: string, method?: string, params?: any): void {
    const key = this.generateKey(url, method, params);
    this.cache.delete(key);
  }

  // Clear all cache entries
  clearAll(): void {
    this.cache.clear();
  }

  // Clear expired entries
  clearExpired(): void {
    for (const [key, entry] of this.cache.entries()) {
      if (!this.isValid(entry)) {
        this.cache.delete(key);
      }
    }
  }

  // Get cache statistics
  getStats(): { size: number; maxSize: number; hitRate?: number } {
    return {
      size: this.cache.size,
      maxSize: this.config.maxSize
    };
  }
}

// Create default cache instance
export const apiCache = new APICache({
  ttl: 5 * 60 * 1000, // 5 minutes default
  maxSize: 100
});

// Cache configuration for different endpoint types
export const CACHE_CONFIG = {
  // Reference data - cache for longer periods
  PLATFORMS: 15 * 60 * 1000, // 15 minutes
  JOB_TYPES: 15 * 60 * 1000, // 15 minutes
  EXTRACTORS: 10 * 60 * 1000, // 10 minutes
  LOADERS: 10 * 60 * 1000, // 10 minutes
  
  // Dynamic data - shorter cache periods
  JOBS: 2 * 60 * 1000, // 2 minutes
  JOB_GROUPS: 2 * 60 * 1000, // 2 minutes
  USER_INFO: 5 * 60 * 1000, // 5 minutes
  
  // Very dynamic data - minimal cache
  JOB_STATUS: 30 * 1000, // 30 seconds
  
  // No cache for mutations
  NO_CACHE: 0
};

export default apiCache;